import "./Info.scss";

import React, { useContext, useRef } from "react";
import clsx from "clsx";

import * as strings from "../../../strings";

import Text from "../../common/Text/Text";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";
import SupportLink from "../../custom/SupportLink/SupportLink";
import { VisibleContext } from "../../../lib/util/ReactUtils";
import { TextElement } from "../../../lib/util/LocaleUtils";
import { IconError, IconInfo, IconTick } from "../../../assets";

export type InfoType = "noData" | "error" | "success" | "loading" | "note";

export interface InfoProps {
	enabled?: boolean;
	heading?: TextElement | React.ReactNode;
	text?: TextElement | React.ReactNode;
	type: InfoType;
	className?: string;
	status?: boolean;
	icon?: React.ReactNode;
	children?: React.ReactNode;
	warning?: boolean;
	noHeading?: boolean;
}

export const TYPE_DEFAULTS: Record<InfoType, InfoProps> = {
	error: {
		type: "error",
		icon: <IconError />,
		heading: strings.MISC_ERROR,
		text: strings.MISC_ERROR_APOLOGIES.map(
			(text) =>
				(
					<>
						{text} <SupportLink />
					</>
				) as any,
		),
	},
	success: {
		type: "success",
		icon: <IconTick />,
		heading: strings.MISC_SUCCESS,
	},
	loading: {
		type: "loading",
		icon: <LoadingSpinner />,
		heading: strings.MISC_LOADING,
	},
	note: {
		type: "note",
		heading: strings.MISC_NOT_ENOUGH_DATA,
		icon: <IconInfo />,
	},
	noData: {
		type: "noData",
		heading: strings.MISC_NOT_ENOUGH_DATA,
	},
};

const Info = (props: InfoProps) => {
	const shownRef = useRef(false);
	const visibleContext = useContext(VisibleContext);

	const {
		icon,
		heading,
		text,
		children,
		status,
		className,
		enabled,
		type,
		warning,
		noHeading,
	} = Object.assign({}, TYPE_DEFAULTS[props.type], props);

	if (visibleContext) {
		shownRef.current = true;
	}

	return shownRef.current ? (
		<div
			className={clsx(
				"Info",
				className,
				status && "--status",
				`--type-${type}`,
				warning && "--warning",
				enabled === false && "--display-none",
			)}
		>
			{icon &&
				React.cloneElement(icon as any, {
					className: clsx("Info__icon", (icon as any).props.className),
				})}
			<div className={"Info__content"}>
				{children || (
					<>
						{heading && !noHeading && (
							<Text tag={"p"} className={"Info__heading"}>
								{heading}
							</Text>
						)}
						{text && (
							<Text tag={"p"} className={"Info__text"}>
								{text}
							</Text>
						)}
					</>
				)}
			</div>
		</div>
	) : null;
};

export default Info;
